import React, { useState, useEffect } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Divider,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Alert,
  Link
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Announcement as AnnouncementIcon,
  Warning as WarningIcon,
  Task as TaskIcon,
  Psychology as PsychologyIcon,
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  Update as UpdateIcon,
  Close as CloseIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axiosInstance from '../services/api';
import notificationService from '../services/notificationService';

const NotificationCenter = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const open = Boolean(anchorEl);

  useEffect(() => {
    fetchUnreadCount();
    // Refresh unread count every minute
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, currentTab]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axiosInstance.get('/notifications/unread-count');
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error('Lỗi lấy unread count:', error);
    }
  };

  const fetchNotifications = async (reset = true) => {
    try {
      setLoading(true);
      const params = {
        page: reset ? 1 : page,
        limit: 10
      };

      // Tab 0: All, Tab 1: Unread, Tab 2: Read
      if (currentTab === 1) {
        params.unreadOnly = 'true';
      } else if (currentTab === 2) {
        params.readOnly = 'true';
      }

      const response = await axiosInstance.get('/notifications', { params });
      
      if (reset) {
        setNotifications(response.data.notifications);
        setPage(2);
      } else {
        setNotifications(prev => [...prev, ...response.data.notifications]);
        setPage(prev => prev + 1);
      }
      
      setHasMore(response.data.pagination.hasNext);
    } catch (error) {
      console.error('Lỗi lấy notifications:', error);
      notificationService.error('Không thể tải thông báo');
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setNotifications([]);
    setPage(1);
    setCurrentTab(0);
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setNotifications([]);
    setPage(1);
  };

  const markAsRead = async (notificationId, skipRefresh = false) => {
    try {
      await axiosInstance.put(`/notifications/${notificationId}/read`);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n._id === notificationId 
            ? { ...n, recipientStatus: { ...n.recipientStatus, read: true, readAt: new Date() } }
            : n
        )
      );
      
      if (!skipRefresh) {
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Lỗi mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axiosInstance.put('/notifications/mark-all-read');
      
      // Update local notifications state immediately
      setNotifications(prev => 
        prev.map(n => ({
          ...n,
          recipientStatus: { ...n.recipientStatus, read: true, readAt: new Date() }
        }))
      );
      
      // Refresh data from server
      fetchNotifications();
      fetchUnreadCount();
      notificationService.success('Đã đánh dấu tất cả là đã đọc');
    } catch (error) {
      console.error('Lỗi mark all as read:', error);
      notificationService.error('Không thể đánh dấu tất cả');
    }
  };

  const dismissNotification = async (notificationId) => {
    try {
      await axiosInstance.put(`/notifications/${notificationId}/dismiss`);
      
      // Remove from local state
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      fetchUnreadCount();
      notificationService.success('Đã ẩn thông báo');
    } catch (error) {
      console.error('Lỗi dismiss notification:', error);
      notificationService.error('Không thể ẩn thông báo');
    }
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read if not already read
    if (!notification.recipientStatus?.read) {
      await markAsRead(notification._id, false); // Don't skip refresh
    }

    // Handle action
    if (notification.actionType === 'navigate' && notification.actionUrl) {
      window.location.href = notification.actionUrl;
    } else if (notification.actionType === 'external_link' && notification.actionUrl) {
      window.open(notification.actionUrl, '_blank');
    } else if (notification.actionType === 'modal') {
      setSelectedNotification(notification);
      setDetailDialogOpen(true);
    } else {
      // Show detail dialog for notifications without action
      setSelectedNotification(notification);
      setDetailDialogOpen(true);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'report_reminder':
        return <AssignmentIcon />;
      case 'google_update':
        return <UpdateIcon />;
      case 'system_announcement':
        return <AnnouncementIcon />;
      case 'website_alert':
        return <WarningIcon />;
      case 'task_assignment':
        return <TaskIcon />;
      case 'project_update':
        return <TrendingUpIcon />;
      case 'kpi_deadline':
        return <ScheduleIcon />;
      case 'maintenance':
        return <BuildIcon />;
      case 'feature_update':
        return <PsychologyIcon />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'success':
        return 'success';
      case 'info':
      default:
        return 'info';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return '#d32f2f';
      case 'high':
        return '#f57c00';
      case 'medium':
        return '#1976d2';
      case 'low':
      default:
        return '#616161';
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Vừa xong';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)} giờ trước`;
    } else {
      return date.toLocaleDateString('vi-VN');
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchNotifications(false);
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        sx={{ mr: 1 }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { width: 420, maxHeight: 600 }
        }}
      >
        <Box>
          {/* Header */}
          <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              🔔 Thông báo
            </Typography>
            <Box>
              <IconButton size="small" onClick={() => fetchNotifications()}>
                <RefreshIcon />
              </IconButton>
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Divider />

          {/* Tabs */}
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ minHeight: 'auto' }}
          >
            <Tab label="Tất cả" sx={{ minHeight: 48 }} />
            <Tab 
              label={
                <Box display="flex" alignItems="center">
                  Chưa đọc
                  {unreadCount > 0 && (
                    <Chip 
                      label={unreadCount} 
                      color="error" 
                      size="small" 
                      sx={{ ml: 1 }} 
                    />
                  )}
                </Box>
              }
              sx={{ minHeight: 48 }}
            />
            <Tab label="Đã đọc" sx={{ minHeight: 48 }} />
          </Tabs>

          {/* Action Bar */}
          {unreadCount > 0 && (
            <Box p={1} bgcolor="action.hover">
              <Button
                size="small"
                onClick={markAllAsRead}
                startIcon={<MarkEmailReadIcon />}
                fullWidth
              >
                Đánh dấu tất cả đã đọc
              </Button>
            </Box>
          )}

          {/* Notifications List */}
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {loading && notifications.length === 0 && (
              <Box p={3} textAlign="center">
                <CircularProgress />
              </Box>
            )}

            {!loading && notifications.length === 0 && (
              <Box py={4} textAlign="center">
                <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="textSecondary">
                  {currentTab === 1 ? 'Không có thông báo chưa đọc' : 
                   currentTab === 2 ? 'Không có thông báo đã đọc' : 
                   'Không có thông báo'}
                </Typography>
              </Box>
            )}

            <List dense>
              {notifications.map((notification) => (
                <ListItem
                  key={notification._id}
                  alignItems="flex-start"
                  sx={{
                    bgcolor: notification.recipientStatus?.read ? 'transparent' : 'action.hover',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <ListItemIcon>
                    <Avatar
                      sx={{
                        bgcolor: notification.color || getPriorityColor(notification.priority),
                        width: 36,
                        height: 36
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Typography 
                          variant="subtitle2" 
                          component="div" 
                          sx={{ 
                            fontWeight: notification.recipientStatus?.read ? 'normal' : 'bold',
                            flex: 1,
                            mr: 1
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Chip
                            label={notification.priority.toUpperCase()}
                            color={getSeverityColor(notification.severity)}
                            size="small"
                            variant="outlined"
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissNotification(notification._id);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography 
                          variant="body2" 
                          color="textSecondary" 
                          sx={{ 
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                          <Typography variant="caption" color="textSecondary">
                            {formatTime(notification.createdAt)}
                          </Typography>
                          {notification.actionLabel && (
                            <Typography variant="caption" color="primary">
                              {notification.actionLabel} →
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>

            {/* Load More */}
            {hasMore && !loading && notifications.length > 0 && (
              <Box p={2} textAlign="center">
                <Button onClick={loadMore} size="small">
                  Tải thêm
                </Button>
              </Box>
            )}

            {loading && notifications.length > 0 && (
              <Box p={2} textAlign="center">
                <CircularProgress size={20} />
              </Box>
            )}
          </Box>
        </Box>
      </Popover>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedNotification && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ bgcolor: selectedNotification.color || getPriorityColor(selectedNotification.priority) }}>
                  {getNotificationIcon(selectedNotification.type)}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6">{selectedNotification.title}</Typography>
                  <Box display="flex" gap={1} mt={0.5}>
                    <Chip 
                      label={selectedNotification.type.replace('_', ' ')} 
                      size="small" 
                      variant="outlined"
                    />
                    <Chip 
                      label={selectedNotification.priority} 
                      color={getSeverityColor(selectedNotification.severity)}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <Typography variant="body1" gutterBottom>
                {selectedNotification.message}
              </Typography>
              
              {selectedNotification.description && (
                <Alert severity={getSeverityColor(selectedNotification.severity)} sx={{ mt: 2 }}>
                  {selectedNotification.description}
                </Alert>
              )}

              {selectedNotification.attachments && selectedNotification.attachments.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    📎 Tệp đính kèm:
                  </Typography>
                  {selectedNotification.attachments.map((attachment, index) => (
                    <Box key={index} display="flex" alignItems="center" gap={1} mt={1}>
                      <DownloadIcon />
                      <Link href={attachment.url} target="_blank" rel="noopener">
                        {attachment.name}
                      </Link>
                    </Box>
                  ))}
                </Box>
              )}

              <Typography variant="caption" color="textSecondary" display="block" mt={2}>
                Thời gian: {new Date(selectedNotification.createdAt).toLocaleString('vi-VN')}
              </Typography>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={() => setDetailDialogOpen(false)}>
                Đóng
              </Button>
              {selectedNotification.actionUrl && (
                <Button
                  variant="contained"
                  startIcon={selectedNotification.actionType === 'external_link' ? <OpenInNewIcon /> : null}
                  onClick={() => {
                    if (selectedNotification.actionType === 'external_link') {
                      window.open(selectedNotification.actionUrl, '_blank');
                    } else {
                      window.location.href = selectedNotification.actionUrl;
                    }
                  }}
                >
                  {selectedNotification.actionLabel || 'Xem chi tiết'}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default NotificationCenter; 